<template>
  <v-menu v-model="computedValue" v-bind="{ ...menuStaticBind, ...menuBind }" :content-class="menuContentClass">
    <template #activator="activatorData" v-if="!hideActivator">
      <slot name="activator" v-bind="activatorData">
        <template v-if="type === 'tool'">
          <base-button
            size="sm"
            :type="computedValue ? 'alternative' : 'text'"
            :toggled="computedValue"
            :icon="buttonBind.icon"
            :tooltip="buttonBind.tooltip || null"
            v-on="{ ...activatorData.on, ...$listeners }"
            v-bind="{ ...buttonBind, loading }"
          />
        </template>
        <template v-else-if="type === 'menu'">
          <component
            :is="menuData.subMenu ? 'TheNavbarTopPanelMenuItem' : 'TheNavbarTopPanelMenuButton'"
            v-bind="{ ...buttonBind }"
            v-on="{ ...activatorData.on, ...$listeners }"
            :class="{ 'background-icon': computedValue }"
          />
        </template>
      </slot>
    </template>
    <v-card
      :loading="loading"
      :class="contentClass"
      class="tw:flexcol-0"
      :style="
        hasFooter
          ? 'padding-bottom: 50px'
          : `margin-bottom: ${position === 'left' && hasDocumentation && documentationPosition === 'footer' ? '25px' : '0'}`
      "
    >
      <template v-if="hasCloseHeader && position === 'top'">
        <v-row no-gutters class="pl-4 pr-1 py-1" align="center">
          <slot name="content.header.prepend" />
          <v-col cols="auto" v-if="hasDocumentation && documentationPosition === 'header'">
            <documentation-button :name="menuData.name" />
          </v-col>
          <v-spacer />
          <v-col cols="auto">
            <base-button type="text" size="sm" icon="mdi-close" @click="computedValue = false" />
          </v-col>
        </v-row>
      </template>
      <template v-else-if="hasCloseHeader && position === 'left'">
        <div style="position: sticky; top: 0; background-color: #fff; z-index: 2">
          <div class="py-4 px-2 d-flex justify-space-between">
            <span class="d-flex align-center" style="gap: 0.5rem">
              <span>{{ buttonBind.verboseName }}</span>
              <documentation-button
                :name="menuData.name"
                v-if="hasDocumentation && documentationPosition === 'header'"
              />
              <slot name="content.header.append" />
            </span>
            <base-button type="text" icon="mdi-close" @click="computedValue = false" />
          </div>
          <v-divider></v-divider>
        </div>
      </template>
      <slot name="content" />
    </v-card>
    <template v-if="hasFooter && position === 'left'">
      <div class="navbar-menu-tool-sticky-buttons d-flex align-center">
        <slot name="content.footer" />
      </div>
    </template>
    <div class="navbar-menu-documentation" v-if="hasDocumentation && documentationPosition === 'footer'">
      <documentation-button :name="menuData.name" />
    </div>
  </v-menu>
</template>
<script>
import { get } from 'vuex-pathify';
import DocumentationButton from '@/components/DocumentationButton';
import TheNavbarTopPanelMenuButton from '@/components/TheNavbarTopPanelMenuButton';
import TheNavbarTopPanelMenuItem from '@/components/TheNavbarTopPanelMenuItem';
import documentation from '@/mixins/documentation';
export default {
  name: 'NavbarMenuTool',
  components: { DocumentationButton, TheNavbarTopPanelMenuItem, TheNavbarTopPanelMenuButton },
  mixins: [documentation],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    menuData: {
      type: Object,
      required: true,
    },
    buttonBind: {
      type: Object,
      default: () => {
        return {};
      },
    },
    menuBind: {
      type: Object,
      default: () => {
        return {};
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'tool',
      validator: v => ['tool', 'menu'].includes(v),
    },
    hasCloseHeader: {
      type: Boolean,
      default: false,
    },
    hasFooter: {
      type: Boolean,
      default: false,
    },
    unfillWidth: {
      type: Boolean,
      default: false,
    },
    hideActivator: {
      type: Boolean,
      default: false,
    },
    documentationPosition: {
      type: String,
      default: 'footer',
      validator: v => ['header', 'footer'].includes(v),
    },
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(nV) {
        this.$emit('input', nV);
      },
    },
    activeMenus: get('navbarMenus/activeMenus'),
    contentClass() {
      return `navbar-menu-tool-content-${this.position}`;
    },
    menuContentClass() {
      return `
        navbar-menu-tool
        navbar-menu-tool-${this.position}
        navbar-menu-tool-${this.position}-${this.subposition}
        ${this.unfillWidth ? '' : `navbar-menu-tool-${this.position}-fill-width`}`;
    },
    menuStaticBind() {
      return {
        closeOnClick: false,
        closeOnContentClick: false,
        attach: '.map',
        offsetY: true,
      };
    },
    position() {
      return this.menuData.position || 'top';
    },
    subposition() {
      return this.menuData.subposition || 'left';
    },
    isCurrentMenuActive() {
      return this.activeMenus.find(item => item.name === this.menuData.name);
    },
    hasDocumentation() {
      return this.documentation[this.menuData.name];
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return { isCurrentMenuActive: vm.isCurrentMenuActive, isMenuOpen: vm.computedValue };
      },
      (nV, oV) => {
        const { isCurrentMenuActive: nIsCurrentMenuActive, isMenuOpen: nIsMenuOpen } = nV;
        const { isCurrentMenuActive: oIsCurrentMenuActive, isMenuOpen: oIsMenuOpen } = oV;
        if (oIsMenuOpen !== nIsMenuOpen)
          this.$store.commit('navbarMenus/SET_ACTIVE_TOOL', { data: this.menuData, value: nIsMenuOpen });
        if (oIsCurrentMenuActive && !nIsCurrentMenuActive) this.computedValue = false;
      }
    );
  },
};
</script>
<style lang="scss" scoped>
$panel-edge-margin: 16px;

.background-icon {
  @include activeToolButtonStyle();
  ::v-deep {
    .v-subheader {
      color: map-get($colors, 'primary') !important;
    }
  }
}
::v-deep {
  .active-icon {
    color: map-get($colors, 'link') !important;
  }
}
/** https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_display/Containing_block#identifying_the_containing_block */
:global(.navbar-menu-tool) {
  contain: unset !important;
}

:deep(.v-card .v-card__progress > .v-progress-linear) {
  z-index: 3 !important;
}

.navbar-menu-tool-top {
  top: 0 !important;
  overflow: hidden;
}
.navbar-menu-tool-top-left {
  left: $panel-edge-margin !important;
}
.navbar-menu-tool-top-right {
  left: unset !important;
  right: $panel-edge-margin !important;
}
body:has(.sider-menu__expanded) .navbar-menu-tool-top-fill-width {
  min-width: calc(
    100dvw - #{$sidebar-width__expanded} - #{$sider-menu__expanded} - (#{$panel-edge-margin} * 2)
  ) !important;
  width: calc(100dvw - #{$sidebar-width__expanded} - #{$sider-menu__expanded} - (#{$panel-edge-margin} * 2)) !important;
}
body:has(.sider-menu__collapsed) .navbar-menu-tool-top-fill-width {
  min-width: calc(
    100dvw - #{$sidebar-width__expanded} - #{$sider-menu__collapsed} - (#{$panel-edge-margin} * 2)
  ) !important;
  width: calc(
    100dvw - #{$sidebar-width__expanded} - #{$sider-menu__collapsed} - (#{$panel-edge-margin} * 2)
  ) !important;
}
.navbar-menu-tool-content-top {
  max-height: 120px !important;
}
.navbar-menu-tool-left {
  top: 125px !important;
  left: $panel-edge-margin !important;
  min-width: calc(25vw - (#{$panel-edge-margin} * 2)) !important;
  width: calc(25vw - (#{$panel-edge-margin} * 2)) !important;
  height: 59vh !important;
  display: flex;
  flex-direction: column;
  background-color: white;
}
.navbar-menu-tool-content-left {
  flex-grow: 1;
  overflow-y: auto;
  box-shadow: unset !important;
}
.navbar-menu-tool-sticky-buttons {
  position: absolute;
  bottom: 0;
  height: 50px;
  width: 100%;
  background-color: #fff;
  z-index: 2;
  box-shadow: 0px -3px 6px rgba(0, 0, 0, 0.15);
}
.navbar-menu-documentation {
  position: absolute;
  bottom: 2px;
  right: 2px;
  z-index: 2;
}
</style>
